import React from 'react';
import styles from './WholesalerReportsAnalytics.module.scss';
import { Container, Row, Col, Button, Input, FormGroup, Label, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import DataTable from 'react-data-table-component';
import {
    customStyles,
    demographicsColumns,
    demographicsData,
    financialColumns,
    financialData,
    inventoryReportsColumns,
    inventoryReportsData,
    marketingColumns,
    marketingData,
    metricsData,
    salesReportColumns,
    salesReportData
} from './reportsAnalyticsData';

const WholesalerReportsAnalytics = () => {
    const [dropdownOpen, setDropdownOpen] = React.useState(false);

    const toggleDropdown = () => setDropdownOpen((prevState) => !prevState);

    return (
        <div className={styles.mainContainer}>
            <h4 className={styles.mainHeading}>Reports & Analytics</h4>

            {/* Metrics Section */}
            <h4 className={styles.sectionHeading}>Metrics</h4>
            <Row className={styles.metricsRow}>
                {metricsData.map((item, index) => (
                    <Col key={index} sm="6" lg="4" xl="3" xxl="2" className={styles.metricsColumn}>
                        <div className={styles.infoCard}>
                            <span>{item.value}</span> {item.label}
                        </div>
                    </Col>
                ))}
            </Row>

            {/* Sales Reports Section */}
            <h4 className={styles.sectionHeading}>Sales Reports</h4>
            <Row className={styles.salesReportRow}>
                <Col xxl={6} className="pe-xxl-2">
                    <DataTable
                        columns={salesReportColumns}
                        data={salesReportData}
                        highlightOnHover
                        customStyles={customStyles}
                    />
                </Col>
            </Row>

            <Row className={styles.salesReportRow}>
                <Col xl={6} className="pe-xl-2">
                    <div className={styles.graphContainer}>
                        <h5>Product Performance</h5>
                        <div className={styles.graphWrapper}>
                            <p>Bar Graph: Showing the sales numbers of top-selling products over a specific period.</p>
                        </div>
                    </div>
                </Col>
                <Col xl={6} className="ps-xl-2">
                    <div className={styles.graphContainer}>
                        <h5>Customer Review Score</h5>
                        <div className={styles.graphWrapper}>
                            <p>
                                Line Graph: Displaying the average customer review score for each product, allowing
                                for a quick comparison of sales and satisfaction.
                            </p>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Inventory Reports Section */}
            <h4 className={styles.sectionHeading}>Inventory Reports</h4>
            <Container fluid className={styles.scheduleContainer}>
                <div className={styles.table}>
                    <DataTable
                        columns={inventoryReportsColumns}
                        data={inventoryReportsData}
                        highlightOnHover
                        customStyles={customStyles}
                    />
                </div>
            </Container>

            <Container fluid className={styles.stockMovementContainer}>
                <div className={styles.graphContainer}>
                    <h5>Stock Movement</h5>
                    <div className={styles.graphWrapper}>
                        <p>
                            Line Graph: Showing the stock levels of key products over time, helping identify trends
                            such as seasonal fluctuations or the impact of marketing campaigns on inventory levels.
                        </p>
                    </div>
                </div>
            </Container>

            {/* Customer Reports Section */}
            <h4 className={styles.sectionHeading}>Customer Reports</h4>
            <Container fluid className={styles.scheduleContainer}>
                <div className={styles.table}>
                    <DataTable
                        title={"Customer Demographics"}
                        columns={demographicsColumns}
                        data={demographicsData}
                        highlightOnHover
                        customStyles={customStyles}
                    />
                </div>
            </Container>

            <Row className={styles.salesReportRow}>
                <Col xl={6} className="pe-xl-2">
                    <div className={styles.graphContainer}>
                        <h5>Customer Loyalty</h5>
                        <div className={styles.graphWrapper}>
                            <p>Pie Chart: Shows the distribution of first-time customers versus repeat customers.</p>
                        </div>
                    </div>
                </Col>
                <Col xl={6} className="ps-xl-2">
                    <div className={styles.graphContainer}>
                        <h5>Customer Loyalty</h5>
                        <div className={styles.graphWrapper}>
                            <p>Stacked Bar Graph: Displays the number of purchases per customer segment (e.g., 1-time buyers, 2-3 times, 4-5 times, etc.).</p>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Marketing and Promotion Reports Section */}
            <h4 className={styles.sectionHeading}>Marketing and Promotion Reports</h4>
            <Container fluid className={styles.scheduleContainer}>
                <div className={styles.table}>
                    <DataTable
                        title={"Campaign Performance"}
                        columns={marketingColumns}
                        data={marketingData}
                        highlightOnHover
                        customStyles={customStyles}
                    />
                </div>
            </Container>

            <Container fluid className={styles.fullGraphContainer}>
                <div className={styles.graphContainer}>
                    <h5>Promotion Effectiveness</h5>
                    <div className={styles.graphWrapper}>
                        <p>
                            Comparison Graph: Shows sales trends before, during, and after promotional periods. A line
                            graph with shaded areas indicating the promotional periods helps to see the impact of promotions on sales.
                        </p>
                    </div>
                </div>
            </Container>

            {/* Financial Reports Section */}
            <h4 className={styles.sectionHeading}>Financial Reports</h4>
            <Container fluid className={styles.scheduleContainer}>
                <div className={styles.table}>
                    <DataTable
                        title={"Profit and Loss Statement"}
                        columns={financialColumns}
                        data={financialData}
                        highlightOnHover
                        customStyles={customStyles}
                    />
                </div>
            </Container>

            <Container fluid className={styles.fullGraphContainer}>
                <div className={styles.graphContainer}>
                    <h5>Tax Reports</h5>
                    <div className={styles.graphWrapper}>
                        <p>
                            Bar Graph: Shows the sales tax collected in different regions or states over a specific
                            period, helping sellers quickly identify where their tax liabilities are highest.
                        </p>
                    </div>
                </div>
            </Container>

            {/* Custom Report Builder Section */}
            <h4 className={styles.sectionHeading}>Custom Report Builder</h4>
            <Container fluid className={styles.reportBuilderContainer}>
                <div className={styles.sectionTitle}>
                    <h5 className={styles.sectionSubHeading}>Basic Information</h5>
                </div>

                <div className={styles.informationContainer}>
                    <Row className={styles.informationRow}>
                        <Col xxl={7} className="pe-xxl-3">
                            <FormGroup>
                                <Row className={styles.formGroupRow}>
                                    <Col lg={3} className="pe-lg-2">
                                        <Label for="dateFrom">Select date range:</Label>
                                    </Col>
                                    <Col lg={9} className={styles.secondColumn}>
                                        <Input type="date" id="dateFrom" className={styles.dateInput} />
                                        <span className={styles.dateSeparator}>-</span>
                                        <Input type="date" id="dateTo" className={styles.dateInput} />
                                    </Col>
                                </Row>
                            </FormGroup>
                            <FormGroup>
                                <Row className={styles.separateFormGroupRow}>
                                    <Col lg={3} className="pe-lg-2">
                                        <Label for="dateFrom">Choose Metrics:</Label>
                                    </Col>
                                    <Col lg={9} className="mt-lg-3">
                                        <div className={styles.checkboxGroup}>
                                            <Label check>
                                                Total sales
                                                <Input
                                                    type="checkbox"
                                                    name="totalSales"
                                                    className={styles.checkBox}
                                                />
                                            </Label>
                                            <Label check>
                                                Total orders
                                                <Input
                                                    type="checkbox"
                                                    name="totalOrders"
                                                    className={styles.checkBox}
                                                />
                                            </Label>
                                            <Label check>
                                                Average Order Value
                                                <Input
                                                    type="checkbox"
                                                    name="averageOrderValue"
                                                    className={styles.checkBox}
                                                />
                                            </Label>
                                            <Label check>
                                                Conversion Rate
                                                <Input
                                                    type="checkbox"
                                                    name="conversionRate"
                                                    className={styles.checkBox}
                                                />
                                            </Label>
                                            <Label check>
                                                Return on Ad Spend (ROAS)
                                                <Input
                                                    type="checkbox"
                                                    name="roas"
                                                    className={styles.checkBox}
                                                />
                                            </Label>
                                        </div>
                                    </Col>
                                </Row>
                            </FormGroup>
                            <FormGroup>
                                <Row className={styles.separateFormGroupRow}>
                                    <Col lg={3} className="pe-lg-2">
                                        <Label for="dateFrom">Apply Filters:</Label>
                                    </Col>
                                    <Col lg={9} className="mt-lg-3">
                                        <div className={styles.filtersContainer}>
                                            <FormGroup className={styles.formGroup}>
                                                <Row className={styles.formGroupRow}>
                                                    <Col lg={4} className="pe-lg-2">
                                                        <Label for="category">By Product category</Label>
                                                    </Col>
                                                    <Col lg={8}>
                                                        <Input type="select" id="category">
                                                            <option>Choose from dropdown menu</option>
                                                            <option>Choose from dropdown menu</option>
                                                            <option>Choose from dropdown menu</option>
                                                        </Input>
                                                    </Col>
                                                </Row>
                                            </FormGroup>
                                            <FormGroup className={styles.formGroup}>
                                                <Row className={styles.formGroupRow}>
                                                    <Col lg={4} className="pe-lg-2">
                                                        <Label for="category">By Region</Label>
                                                    </Col>
                                                    <Col lg={8}>
                                                        <Input type="select" id="category">
                                                            <option>Choose from dropdown menu</option>
                                                            <option>Choose from dropdown menu</option>
                                                            <option>Choose from dropdown menu</option>
                                                        </Input>
                                                    </Col>
                                                </Row>
                                            </FormGroup>
                                            <FormGroup className={styles.formGroup}>
                                                <Row className={styles.formGroupRow}>
                                                    <Col lg={4} className="pe-lg-2">
                                                        <Label for="category">By Customer Segment</Label>
                                                    </Col>
                                                    <Col lg={8}>
                                                        <Input type="select" id="category">
                                                            <option>Choose from dropdown menu</option>
                                                            <option>Choose from dropdown menu</option>
                                                            <option>Choose from dropdown menu</option>
                                                        </Input>
                                                    </Col>
                                                </Row>
                                            </FormGroup>
                                        </div>
                                    </Col>
                                </Row>
                            </FormGroup>
                            <Container fluid className={styles.actionBtnContainer}>
                                <Button type="submit" color="primary">Generate Report</Button>
                            </Container>
                        </Col>
                        <Col xxl={5} className="ps-xxl-4">
                            <Container fluid className={styles.previewContainer}>
                                <div className={styles.previewBox}>View Preview</div>
                                <Container fluid className={styles.downloadDropdownContainer}>
                                    <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown} className={styles.downloadDropdown}>
                                        <DropdownToggle caret color="secondary" className={styles.downloadDropdownBtn}>Download Report</DropdownToggle>
                                        <DropdownMenu>
                                            <DropdownItem>PDF</DropdownItem>
                                            <DropdownItem>Excel</DropdownItem>
                                        </DropdownMenu>
                                    </Dropdown>
                                </Container>
                            </Container>
                        </Col>
                    </Row>
                </div>
            </Container>
        </div>
    )
}

export default WholesalerReportsAnalytics;